// utils/helpers/helper.filter-sort.js
import _find from 'lodash/find';
import R from 'ramda';
import moment from "utils/moment/momentLocale";

// utils
import {
  getLevelLabel,
  getCommunicationTypeLabel,
} from 'utils/helpers/helper.generateData';
import { DAY_OF_WEEK_OPTIONS } from 'utils/helpers/helper.constant';
import { convertTimeToString, convertDateToString } from 'utils/moment/moment.utils';

const {
  apply,
  map,
  reverse,
  split,
  pipe,
  curry,
  filter,
  ascend,
  descend,
  contains,
  prop,
  path,
  compose,
  sortWith,
  any,
  propEq,
} = R;

// helper functions based on ramda
const makeSortAscendFn = compose(
  ascend,
  pipe(
    split('.'),
    reverse,
    map(prop),
    apply(compose),
  ),
);
const makeSortDescendFn = compose(
  descend,
  pipe(
    split('.'),
    reverse,
    map(prop),
    apply(compose),
  ),
);

const customFilter = val =>
  R.filter(
    R.compose(
      R.any(R.contains(val)),
      R.values,
    ),
  );

const filterBy = curry((transform, selectedValues, collection) =>
  filter(e => selectedValues.includes(transform(e)), collection),
);

const convertObjToArray = obj => {
  let arr = [];
  for (let item in obj) {
    arr.push(obj[item]);
  }
  return arr;
};

const getUniqueArrayObj = (arrayObj, prop) => {
  const result = [];
  const map = new Map();
  for (const item of arrayObj) {
    if (!map.has(item[prop])) {
      map.set(item[prop], true); // set any value to Map
      result.push({ ...item });
    }
  }
  return result;
};

export const getUniqueArrayValue = arrayValues => {
  const result = [];
  const map = new Map();
  for (const item of arrayValues) {
    if (!map.has(item)) {
      map.set(item, true); // set any value to Map
      result.push(item);
    }
  }
  return result;
};

Array.prototype.insert = function(index, item) {
  this.splice(index, 0, item);
};

const getSortedDayOfWeek = weekDays => {
  let result = [];
  weekDays?.map(day => {
    const translatedDays = DAY_OF_WEEK_OPTIONS();
    const dayIndex = translatedDays.findIndex(
      weekDay => weekDay?.value == day?.key,
    );
    const current = {...day, label: translatedDays[dayIndex]?.label || day?.label}
    result[dayIndex] = current;
  });
  return result;
};

const getSortedArrayDayOfWeek = coursArr => {
  let result = [];
  if (coursArr && Array.isArray(coursArr)) {
    const initArr = coursArr
      ?.map(elem => {
        return {
          ...elem,
          name: elem?.subject?.name,
          abbreviation: elem?.subject?.abbreviation,
          color: elem?.subject?.color,
          course_time_range: `${moment(elem?.course_time_start).format(
            'HH:mm',
          )} - ${moment(elem?.course_time_end).format('HH:mm')}`,
        };
      })
      .sort((a, b) => {
        const dayIndexA = DAY_OF_WEEK_OPTIONS().findIndex(
          weekDay => weekDay?.value == a?.course_week,
        );
        const dayIndexB = DAY_OF_WEEK_OPTIONS().findIndex(
          weekDay => weekDay?.value == b?.course_week,
        );
        return dayIndexA - dayIndexB;
      });

      DAY_OF_WEEK_OPTIONS()?.map(day => {
      const dayArr =
        initArr
          .filter(el => el?.course_week?.toString()?.toLowerCase() == day?.value?.toString()?.toLowerCase())
          .sort((a, b) => {
            return a?.course_time_range?.localeCompare(b?.course_time_range);
          }) || [];
      result = [...result, ...dayArr];
    });
  }

  return result;
};

const prepearFilteredOptions = (initList, props) => {
  const hasLIst = initList && Array.isArray(initList);
  if (typeof props === 'string' && hasLIst) {
    if (props.indexOf('.') != -1) {
      const searchPath = split('.', props);
      const options = initList.map(item => {
        if (
          item.hasOwnProperty(searchPath[0]) &&
          item[searchPath[0]] &&
          item[searchPath[0]].hasOwnProperty(searchPath[1])
        )
          return {
            label: item[searchPath[0]][searchPath[1]],
            value: item[searchPath[0]][searchPath[1]],
          };
      });
      // get unique options by property
      return getUniqueArrayObj(options, 'value');
    } else {
      const options = initList.map(item => {
        return {
          label: item[props],
          value: item[props],
        };
      });
      // get unique options by property
      return getUniqueArrayObj(options, 'value');
    }
  } else if (Array.isArray(props) && hasLIst) {
    const result = [];
    const map = new Map();

    for (const prop of props) {
      const isObject = typeof prop === 'object';
      const isLevel = isObject && prop?.group == 'level';
      const isTypes = isObject && prop?.group == 'types';
      const hasGroup = isObject && prop?.group;
      let propsStr = isObject ? prop.key : prop;

      const setToResult = current => {
        let newLabel = current;
        if (isLevel) {
          newLabel = getLevelLabel(current);
        } 
        if (isTypes) {
          newLabel = getCommunicationTypeLabel(current)?.label;
        }
        const newObj = {
          key: current,
          label: newLabel?.label || newLabel?.title || newLabel?.value || newLabel,
          value: current,
          name: hasGroup,
        };
        return newObj;
      };

      if (hasGroup) {
        result.push({
          key: prop.key,
          name: prop.group,
          group: [
            {
              key: prop.group,
              label: prop.label,
              value: `${prop.group}-all`,
              name: `${prop.group}-all`,
            },
          ],
        });

        if (propsStr.indexOf('.') != -1) {
          const searchPath = split('.', propsStr);
          initList.map(item => {
            if (
              item.hasOwnProperty(searchPath[0]) &&
              item[searchPath[0]] &&
              item[searchPath[0]].hasOwnProperty(searchPath[1])
            ) {
              const current = item[searchPath[0]][searchPath[1]];
              result.find((elem, indx) => {
                if (elem.key === propsStr && elem?.group && !!current) {
                  map.set(current, true); // set any value to Map
                  result[indx]['group'].push(setToResult(current));
                }
              });
            }
          });
        } else {
          initList?.map(item => {
            map.set(item[propsStr], true);
            result.find((elem, indx) => {
              if (elem.key === propsStr && elem?.group) {
                if (Array.isArray(item[propsStr])) {
                  if (item[propsStr]?.length > 0) {
                    const arrOfStr = item[propsStr].every(
                      el => typeof el === 'string',
                    );
                    if (arrOfStr) {
                      const oldG = result[indx]['group'];
                      const newArr = item[propsStr].map(el => !!el && setToResult(el));
                      const newGroup = oldG.concat(newArr);
                      result[indx]['group'] = newGroup;
                    } else {
                      const oldG = result[indx]['group'];
                      const newGroup = oldG.concat(item[propsStr]);
                      result[indx]['group'] = newGroup;
                    }
                  }
                } else {
                  !!item?.[propsStr] && result[indx]['group'].push(setToResult(item[propsStr]));
                }
              }
            });
          });
        }
      } else {
        if (propsStr.indexOf('.') != -1) {
          const searchPath = split('.', propsStr);
          initList?.map(item => {
            if (
              item.hasOwnProperty(searchPath[0]) &&
              item[searchPath[0]] &&
              item[searchPath[0]].hasOwnProperty(searchPath[1])
            ) {
              const current = item[searchPath[0]][searchPath[1]];
              if (!!current) {
                map.set(current, true); // set any value to Map
                result.push(setToResult(current));
              }
            }
          });
        } else {
          initList?.map(item => {
            if (!!item?.[propsStr]) {
              map.set(item[propsStr], true);
              result.push(setToResult(item[propsStr]));
            }
          });
        }
      }
    }

    // get unique options by property
    const uniqueGroupResult = result.map((elem, indx) =>
      // get unique options for group by property
      elem.hasOwnProperty('group')
        ? { ...elem, group: getUniqueArrayObj(elem.group, 'value') }
        : elem,
    );

    const results = getUniqueArrayObj(uniqueGroupResult, 'key');

    const singleResult =
      results.filter(el => !el.hasOwnProperty('group')) || [];
    const groupResult = results.filter(el => el.hasOwnProperty('group')) || [];

    const sortedSingleResult = singleResult.sort((a, b) =>
      a?.key?.toString().localeCompare(b?.key),
    );
    const sortedResults = groupResult.map(arrayEl => {
      if (arrayEl.hasOwnProperty('group')) {
        if (arrayEl?.name == 'week') {
          const newGroup = arrayEl?.group;
          const firstEl = newGroup[0];
          newGroup.shift();
          const newDayyOfWeek = getSortedDayOfWeek(newGroup) || [];
          return {
            ...arrayEl,
            group: [firstEl, ...newDayyOfWeek],
          };
        } else {
          const newGroup = arrayEl?.group;
          const firstEl = newGroup[0];
          newGroup.shift();

          const ng = newGroup.sort((a, b) =>
            a?.key?.toString().localeCompare(b?.key),
          );
          return {
            ...arrayEl,
            group: [firstEl, ...ng],
          };
        }
      } else {
        return arrayEl;
      }
    });

    // get unique options by property
    return [...sortedSingleResult, ...sortedResults];
  }
};

/*
 * Filter array by single property
 * prop: string (can be separate with "." (dot))
 * values: array of string
 */
const filterByPropValues = (initArr, prop, values) => {
  if (typeof prop === 'string' && Array.isArray(values)) {
    if (prop.indexOf('.') != -1) {
      const propPath = split('.', prop);
      //const result = R.filter(compose(R.flip(contains)(values), path(propPath)), initArr);
      const result = filterBy(path(propPath), values, initArr);
      return getUniqueArrayObj(result, '_id');
    } else {
      // const result = R.filter(compose(R.flip(contains)(values), R.prop(prop)), initArr);
      const result = filterBy(R.prop(prop), values, initArr);
      return getUniqueArrayObj(result, '_id');
    }
  } else {
    console.error(`values: "${values}", is not array`);
    console.error(`props: "${props}", is not a string`);
    return [];
  }
};

/*
 * Filter array by path of props, they have to be separate with "." (dot)
 * propsPath: array of string
 * value: string
 */
const filterByPropsPath = (initArr, propsPath, value) => {
  /*  const result6 = customFilter(value)(initArr);
   */
  const path = split('.', propsPath);
  const val = value?.toString().toLowerCase();

  const result = initArr.filter(item => {
    if (item.hasOwnProperty(path[0])) {
      if (
        item[path[0]] &&
        item[path[0]].hasOwnProperty(path[1]) &&
        item[path[0]][path[1]]
          ?.toString()
          .toLowerCase()
          .includes(val)
      )
        return item;
    }
  });

  return result;
};

/*
 * Filter array by single property
 * value: string
 * prop: string
 */
const filterByPropValue = (initArr, prop, value) => {
  const val = value?.toString().toLowerCase();

 /*  const result5 = R.filter(
    R.compose(
      R.flip(R.contains)(value),
      R.prop(prop),
    ),
    initArr,
  ); */

  //const result6 = customFilter(value)(initArr);

  const result = initArr.filter(
    item =>
      item[prop]
        ?.toString()
        .toLowerCase()
        .includes(val) && item,
  );

  return result;
};

// props: array od string
// value: string
const filterByPropsValue = (array, props, value) => {
  const result = [];
  const map = new Map();

  const filterBy = curry((transform, selectedValues, collection) =>
    filter(e => selectedValues.includes(transform(e)), collection),
  );

  let predicate = (val, key, obj) => {
    props.map(prop => {
      const isObject = typeof prop === 'object';
      let propsStr = isObject ? prop.key : prop;

      if (propsStr.indexOf('.') != -1) {
        const propPath = split('.', propsStr);
        if (propPath[0] === key) {
          const deep = val[propPath[1]];
          if (deep && deep == value) {
            map.set(obj[propPath[0]][propPath[1]], true); // set any value to Map
            result.push({ ...obj });
            return obj;
          }
        }
      } else {
        if (propsStr === key && val == value) {
          map.set(obj[propsStr], true); // set any value to Map
          result.push({ ...obj });
          return obj;
        }
      }
    });
  };

  R.filter(obj => R.pickBy(predicate, obj))(array);

  return result;
};

const searchElemByProps = (item, propsArr, value) => {
  const val = value?.toString().toLowerCase();
  for (let i = 0; i < propsArr.length; i++) {
    if (propsArr[i].indexOf('.') != -1) {
      const searchPath = split('.', propsArr[i]);

      if (
        searchPath?.length == 2 &&
        item.hasOwnProperty(searchPath[0]) &&
        item[searchPath[0]] &&
        item[searchPath[0]].hasOwnProperty(searchPath[1]) &&
        item[searchPath[0]][searchPath[1]]
          ?.toString()
          .toLowerCase()
          .includes(val)
      ) {
        return item;
      } else if (
        searchPath?.length == 3 &&
        item.hasOwnProperty(searchPath[0]) &&
        item[searchPath[0]] &&
        item[searchPath[0]].hasOwnProperty(searchPath[1]) &&
        item[searchPath[0]][searchPath[1]] &&
        item[searchPath[0]][searchPath[1]].hasOwnProperty(searchPath[2]) &&
        item[searchPath[0]][searchPath[1]][searchPath[2]]
          ?.toString()
          .toLowerCase()
          .includes(val)
      ) {
        return item;
      }
    } else {
      if (propsArr[i] && propsArr[i] == 'students') {
        if (
          item.hasOwnProperty(propsArr[i]) &&
          Array.isArray(item[propsArr[i]])
        ) {
          const propVal = item[propsArr[i]].length;
          const check = Number(propVal) == Number(val);
          if (item.hasOwnProperty(propsArr[i]) && check) return item;
        }
      } else if (propsArr[i] && propsArr[i].indexOf('_time') != -1) {
        if (item.hasOwnProperty(propsArr[i])) {
          const propVal = convertTimeToString(item[propsArr[i]]);
          const check = propVal.indexOf(val) != -1;
          if (item.hasOwnProperty(propsArr[i]) && check) return item;
        }
      } else if (propsArr[i] && propsArr[i].indexOf('Date') != -1) {
        if (item.hasOwnProperty(propsArr[i])) {
          const propVal = convertDateToString(item[propsArr[i]]);
          const check = propVal.indexOf(val) != -1;
          if (item.hasOwnProperty(propsArr[i]) && check) return item;
        }
      } else {
        if (item.hasOwnProperty(propsArr[i])) {
          const propVal = item[propsArr[i]]?.toString().toLowerCase();
          const check = propVal.indexOf(val) != -1;
          if (item.hasOwnProperty(propsArr[i]) && check) return item;
        }
      }
    }
  }
};

const filterByPropsValues = (array, props, values) => {
  const filteredResult = array.filter(chr => {
    const rule = [];
    const amount = values.length;
    for (const prop of props) {
      const isObject = typeof prop === 'object';
      let propRule,
        propsStr = isObject ? prop.key : prop;

      for (const value of values) {
        if (propsStr.includes('.')) {
          const path = split('.', propsStr);
          propRule = R.pathEq(path, value);
        } else {
          propRule = R.propEq(propsStr, value);
        }
        const object = R.find(propRule)([chr]);
        if (object !== undefined) {
          rule.push(propRule);
        }
      }
    }

    const isIncludesRules = R.allPass(rule);
    const res = rule.length >= amount && isIncludesRules(chr);
    return res && chr;
  });

  const unique = getUniqueArrayObj(filteredResult, '_id');
  return unique;
};

const filterByPropsMatchValues = (array, props, values) => {
  const result = [];
  const map = new Map();
  // get object of objects inly with propsBy parameters
  const filteredObject = R.omit([...props], array);
  // conver object of objects to array
  const arr = convertObjToArray(filteredObject);
  // get only unique object
  const uniqueList = getUniqueArrayObj(arr, '_id');

  for (const prop of props) {
    for (const value of values) {
      const val = value?.toString().toLowerCase();

      uniqueList.filter(item => {
        if (prop.indexOf('.') != -1) {
          const propPath = split('.', prop);
          if (item.hasOwnProperty(propPath[0])) {
            if (
              item[propPath[0]] &&
              item[propPath[0]].hasOwnProperty(propPath[1]) &&
              item[propPath[0]][propPath[1]]
                ?.toString()
                .toLowerCase()
                .includes(val)
            ) {
              map.set(item[propPath[0]][propPath[1]], true); // set any value to Map
              result.push({ ...item });
              return item;
            }
          }
        } else {
          if (
            item.hasOwnProperty(prop) &&
            item[prop]
              ?.toString()
              .toLowerCase()
              .includes(val)
          ) {
            map.set(item[prop], true); // set any value to Map
            result.push({ ...item });
            return item;
          }
        }
      });
    }
  }
  // get unique array

  const unique = getUniqueArrayObj(result, '_id');
  return unique;
};

/*
 * Sort list by params in the item object
 * sort.props: array of string
 */

const sortList = (initList, sort) => {
  if (sort.ascend) {
    const props = sort.props.map(prop => makeSortAscendFn(prop));
    return sortWith(props, initList);
  } else {
    const props = sort.props.map(prop => makeSortDescendFn(prop));
    return sortWith(props, initList);
  }
};

/*
 * Search and filter list by particular matches in the params
 * searchProps: string, array of sting
 */
const searchListBy = (initList, searchVal, searchProps) => {
  let strProps = '';
  // get single value
  if (typeof searchProps === 'string') {
    strProps = searchProps;
  } else if (Array.isArray(searchProps)) {
    strProps = searchProps.length == 1 && searchProps[0];
  }

  if (
    typeof searchProps === 'string' ||
    (Array.isArray(searchProps) && searchProps.length == 1)
  ) {
    if (strProps?.indexOf('.') != -1) {
      return filterByPropsPath(initList, strProps, searchVal);
    } else {
      return filterByPropValue(initList, strProps, searchVal);
    }
  } else if (Array.isArray(searchProps) && searchProps.length > 1) {
    return initList.filter(item =>
      searchElemByProps(item, searchProps, searchVal),
    );
  }
};

/*
 * Filter list by particular matches in the params
 * propsBy: string, array of sting
 * filterValArr: string, array of sting
 */
const filterListBy = (initList, propsBy, filterValArr) => {
  let strValue = '';
  // get single value
  if (typeof filterValArr === 'string') {
    strValue = filterValArr;
  } else if (Array.isArray(filterValArr)) {
    strValue = filterValArr.length == 1 && filterValArr[0];
  }

  if (
    typeof propsBy === 'string' ||
    (Array.isArray(propsBy) && propsBy.length == 1)
  ) {
    let strProps = '';
    if (typeof propsBy === 'string') {
      strProps = propsBy;
    } else if (Array.isArray(propsBy) && propsBy.length == 1) {
      strProps = typeof propsBy[0] == 'object' ? propsBy[0].key : propsBy[0];
    }
    if (filterValArr?.length == 1) {
      if (strProps.indexOf('.') != -1) {
        return filterByPropsPath(initList, strProps, strValue);
      } else {
        return filterByPropValue(initList, strProps, strValue);
      }
    } else if (filterValArr.length > 1) {
      return filterByPropValues(initList, strProps, filterValArr);
    }
  } else if (Array.isArray(propsBy) && propsBy.length > 1) {
    if (filterValArr.length == 1) {
      return filterByPropsValue(initList, propsBy, strValue);
    } else if (filterValArr.length > 1) {
      return filterByPropsValues(initList, propsBy, filterValArr);
    }
  }
};

const filterGroupedOptionsByValue = (array, inputStr) => {
  if (Array.isArray(array) && array.length > 0) {
    if ( inputStr ) {
      return array?.filter(option => {
        if (option?.group) {
          const groupOpt = option.options?.filter(option => {
            return (
              (option?.title &&
                option?.title
                  .toString()
                  .toLowerCase()
                  .includes(inputStr?.toString().toLowerCase())) ||
              (option?.label &&
                option?.label
                  ?.toString()
                  .toLowerCase()
                  .includes(inputStr?.toString().toLowerCase()))
            );
          });
          return groupOpt.length > 0 && Object.assign(option, {
            options: groupOpt,
          });
        } else {
          return (
            (option?.title &&
              option?.title
                ?.toString()
                .toLowerCase()
                .includes(inputStr?.toString().toLowerCase())) ||
            (option?.label &&
              option?.label
                ?.toString()
                .toLowerCase()
                .includes(inputStr?.toString().toLowerCase()))
          );
        }
      });
    } else {
      return array;
    }
  } else {
    return [];
  }
}

const getUniqueGroupedOptions = array => {
  if (Array.isArray(array) && array.length > 0) {
    let result = [];
    const map = new Map();
    for (const option of array) {
      if (typeof option === 'object') {
        if (option?.group) {
          if (!map.has(option?.group)) {
            map.set(option?.group, true); // set any value to Map
            result.push(option);
          } else {
            const index = result.findIndex(po => po?.group == option?.group);
            const unique = getUniqueArrayObj(
              [...result[index]['options'], ...option?.options],
              'key',
            );
            result[index]['options'] = unique;
          }
        } else {
          if (!map.has(option)) {
            map.set(option, true); // set any value to Map
            result.push(option);
          }
        }
      } else {
        if (!map.has(option)) {
          map.set(option, true); // set any value to Map
          result.push(option);
        }
      }
    }
    return result;
  } else {
    return [];
  }
};

export {
  makeSortAscendFn,
  makeSortDescendFn,
  sortList,
  searchListBy,
  filterListBy,
  getUniqueArrayObj,
  prepearFilteredOptions,
  searchElemByProps,
  getSortedArrayDayOfWeek,
  filterGroupedOptionsByValue,
  getUniqueGroupedOptions,
};